import type {
  MeetingsResponse,
  MeetingSyncStatus,
} from "../../shared/meetings";
import {
  addCalendarDays,
  calendarMonthName,
  melbourneDate,
  melbourneToday,
  weekOf,
} from "../../shared/dates";
const pages = new Map<string, MeetingsResponse>();
let epoch = 0;
export function meetingCacheEpoch() {
  return epoch;
}
export function cachedMeetings(key: string): MeetingsResponse | null {
  return pages.get(key) ?? null;
}
export function rememberMeetings(
  key: string,
  value: MeetingsResponse,
  requestEpoch: number,
) {
  if (requestEpoch !== epoch) return;
  if (pages.size >= 8) pages.delete(pages.keys().next().value!);
  pages.set(key, value);
}
/** No meeting rows, notes or transcripts go into local/session storage. */
export function clearMeetingCaches() {
  epoch++;
  pages.clear();
}
export function meetingDate(ms: number, full = false): string {
  return new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Melbourne",
    day: "numeric",
    month: "long",
    ...(full ? { year: "numeric" } : {}),
  }).format(ms);
}
/** "9 Oct" — the project page's Meetings group. */
export function meetingShortDate(ms: number): string {
  return new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Melbourne",
    day: "numeric",
    month: "short",
  }).format(ms);
}
export function meetingTime(ms: number): string {
  return new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Melbourne",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
    .format(ms)
    .toLowerCase();
}
/** "Mon 19 Oct" — the list's mono date column and the detail sub-line. */
export function meetingDay(ms: number): string {
  return new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Melbourne",
    weekday: "short",
    day: "numeric",
    month: "short",
  })
    .format(ms)
    .replace(/,/g, "");
}
/** "45m" — compact duration for list rows; an em dash when unknown. */
export function meetingShortDuration(seconds: number | null): string {
  return seconds === null ? "—" : `${Math.round(seconds / 60)}m`;
}
/** List group for a recording, on Melbourne Mon–Sun weeks: This week /
    Last week / Earlier in <Month> / <Month YYYY>. */
export function meetingBucket(ms: number, today = melbourneToday()): string {
  const day = melbourneDate(ms);
  const thisWeek = weekOf(today).start;
  if (day >= thisWeek) return "This week";
  if (day >= addCalendarDays(thisWeek, -7)) return "Last week";
  const month = day.slice(0, 7);
  return month === today.slice(0, 7)
    ? `Earlier in ${calendarMonthName(month)}`
    : `${calendarMonthName(month)} ${month.slice(0, 4)}`;
}
export function meetingDuration(seconds: number | null): string {
  return seconds === null
    ? "Duration unavailable"
    : `${Math.round(seconds / 60)} min`;
}
/** Header-right status: "Fathom · synced 9:42 am" (or with a date when the
    last successful sync wasn't today). Never claims a sync on a local read. */
export function meetingSyncLabel(status: MeetingSyncStatus | null): string {
  if (!status) return "Fathom · checking…";
  if (status.state === "disconnected") return "Fathom not connected";
  if (status.state === "failed") return "Fathom sync failed";
  if (status.state === "importing") return "Fathom · importing history…";
  if (status.state === "syncing") return "Fathom · syncing…";
  if (!status.last_sync_at) return "Fathom · waiting for first sync";
  const at = status.last_sync_at;
  return melbourneDate(at) === melbourneToday()
    ? `Fathom · synced ${meetingTime(at)}`
    : `Fathom · synced ${meetingShortDate(at)}, ${meetingTime(at)}`;
}
export function safeMeetingLink(value: string): string {
  try {
    const url = new URL(value);
    return ["https:", "mailto:"].includes(url.protocol) &&
      !url.username &&
      !url.password
      ? value
      : "";
  } catch {
    return "";
  }
}

/** One line of plain text for list rows. Provider excerpts are Markdown;
    heading lines are section labels ("## Planning notes"), not content, so
    they are dropped rather than run into the sentence that follows. */
export function meetingExcerpt(text: string): string {
  const plain = (source: string) =>
    source
      .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
      .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
      .replace(/^\s*>\s?/gm, "")
      .replace(/^\s*(?:[-*+]|\d+[.)])\s+/gm, "")
      .replace(/^[ \t]*#{1,6}(?:[ \t]+|$)/gm, "")
      .replace(/<((?:https?:\/\/|mailto:)[^<>\s]+|[^<>\s@]+@[^<>\s@]+)>/gi, "$1")
      .replace(/<\/?[A-Za-z][^<>]*>/g, "")
      .replace(/[*_`~]/g, "")
      .replace(/\s+/g, " ")
      .trim();
  // A summary that is nothing but headings still gets a line.
  return plain(text.replace(/^[ \t]*#{1,6}(?:[ \t]+.*)?$/gm, "")) || plain(text);
}
